/**
 * 시험지 폴더 트리의 '펼침 상태'를 브라우저 세션이 쥔다 (sessionStorage).
 *
 * 원래 이 값은 서버에 있었다 — 접고 펼 때마다 PATCH /folders/{id} {is_open} 이 나갔다.
 * 폴더가 공유되면 그 자리가 무너진다: 남이 접은 것이 내 화면을 바꾼다. 보기 상태는 사람마다
 * 다른 것이라 공유되는 값에 얹을 수 없다(설계 정본 §4 FS-01). 왕복이 없어지므로 낙관적 갱신·
 * 되돌리기·순번 관리도 함께 사라진다.
 *
 * 세션인 이유는 오너 지시가 「일시 저장」이기 때문이다 — 탭을 닫으면 사라지고 새 탭은 기본
 * 상태로 시작한다. 영구 보관이 필요해지면 이 파일 두 함수의 저장 위치만 바꾸면 된다.
 *
 * 키 규칙은 이 저장소 선례를 따른다 — 'aitracker:timeline:v1:' (src/utils/aitrackerTimelineCache.js:15).
 * 계정을 키에 넣는 이유: 같은 탭에서 계정을 바꿔 로그인해도 남의 화면 상태를 물려받지 않게.
 *
 * 이 파일은 어떤 경우에도 던지지 않는다. 사생활 보호 모드처럼 저장소 접근 자체가 예외인
 * 환경에서도 화면은 그 세션 동안 기본 상태로 멀쩡히 동작해야 한다 — 접고 펴는 것 자체는 된다.
 * 오류 문구도 띄우지 않는다(설계 §4 FS-01).
 */

const PREFIX = 'examPapers:folderOpen:v1:';
const VERSION = 1;

const keyOf = (userId) => `${PREFIX}${userId || 'anon'}`;

// id 는 문자열로 통일한다 — 서버가 숫자로 주고 화면이 문자열로 비교하는 자리가 섞여 있다
// (sameSel·folderIdOf 가 String() 으로 맞춰 쓰는 것과 같은 이유).
// null·빈 문자열은 아예 담지 않는다: 이 트리는 id 없는 노드를 상정하고 있어(key={id ?? name}),
// "null" 이 저장되면 서로 다른 무명 폴더가 세션에서 한 칸을 공유한다.
function normalizeId(id) {
  if (typeof id === 'number') return Number.isFinite(id) ? String(id) : null;
  if (typeof id === 'string') return id === '' ? null : id;
  return null;
}

// Set 과 배열을 같이 받는다 — 부르는 쪽이 펼침 집합(Set)을 그대로 넘길 수 있게.
function toArray(v) {
  if (Array.isArray(v)) return v;
  if (v && typeof v.forEach === 'function') return Array.from(v);
  return [];
}

// 무리(내 폴더 / 공유받은 폴더) 접힘은 값이 없으면 둘 다 펼침이다 — 처음 여는 사람에게
// 지금과 같은 화면을 보여야 하기 때문이다. 참·거짓이 아닌 값은 못 믿으니 기본값으로 되돌린다.
function normalizeGroups(g) {
  const src = g && typeof g === 'object' ? g : {};
  return {
    mine: typeof src.mine === 'boolean' ? src.mine : true,
    shared: typeof src.shared === 'boolean' ? src.shared : true,
  };
}

/**
 * 세션에 담긴 펼침 상태를 읽는다.
 *
 * 저장된 것이 없으면 **null** 이다. 「비어 있음(전부 접음)」과 반드시 구별돼야 부르는 쪽이
 * 씨앗(서버 is_open 이 참인 폴더)을 심을 수 있다 — 빈 집합으로 뭉개면 사용자가 전부 접은
 * 순간 씨앗이 다시 심겨 다음 렌더에 도로 펼쳐진다.
 *
 * 깨진 JSON · 형태가 다른 값 · 판 번호가 다른 값 · 저장소 접근 자체가 예외인 환경은 전부 null.
 *
 * @returns {{folders: Set<string>, groups: {mine: boolean, shared: boolean}}|null}
 */
export function readOpenState(userId) {
  try {
    const raw = sessionStorage.getItem(keyOf(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== VERSION || !Array.isArray(parsed.folders)) return null;
    const folders = new Set();
    parsed.folders.forEach((id) => {
      const key = normalizeId(id);
      if (key != null) folders.add(key);
    });
    return { folders, groups: normalizeGroups(parsed.groups) };
  } catch (_) {
    return null;
  }
}

/**
 * 펼침 상태를 세션에 쓴다. state = { folders: Set|배열, groups: {mine, shared} }.
 * 저장 형태는 { v: 1, folders: [...], groups: {...} } — 폴더와 무리를 한 자리에 담는다.
 *
 * liveIds(배열 또는 Set)를 주면 그 안에 있는 id 만 남긴다 — 지웠다 만들기를 반복해도 죽은
 * 폴더 id 가 세션에 무한히 쌓이지 않게. 대신 **빈 목록을 넘기면 전부 지워진다**:
 * 조회에 실패해 목록이 빈 상태에서 부르면 안 되고, 성공한 load 뒤에만 부른다.
 * liveIds 를 안 주면 가지치기 없이 그대로 저장한다.
 *
 * 저장 실패(용량 초과·사생활 보호 모드)는 조용히 삼킨다 — 펼침은 잃어도 되는 값이고,
 * 이것 때문에 화면에 오류를 띄우면 얻는 것보다 잃는 것이 크다.
 *
 * @returns {boolean} 저장에 성공했는지(선례: aitrackerTimelineCache.saveEnvelope). 안 봐도 된다.
 */
export function writeOpenState(userId, state, liveIds) {
  try {
    let live = null;
    if (liveIds != null) {
      live = new Set();
      toArray(liveIds).forEach((id) => {
        const key = normalizeId(id);
        if (key != null) live.add(key);
      });
    }
    const folders = [];
    const seen = new Set();
    toArray(state && state.folders).forEach((id) => {
      const key = normalizeId(id);
      if (key == null || seen.has(key)) return;
      if (live && !live.has(key)) return;
      seen.add(key);
      folders.push(key);
    });
    const payload = { v: VERSION, folders, groups: normalizeGroups(state && state.groups) };
    sessionStorage.setItem(keyOf(userId), JSON.stringify(payload));
    return true;
  } catch (_) {
    return false;
  }
}
